import { authenticatedRequest } from "./client";

export interface NationalArchivesSearchParams {
  query?: string;
  page?: number;
  pageSize?: number;
  // Advanced search options
  allWords?: string;
  exactPhrase?: string;
  anyWords?: string;
  excludingWords?: string;
  reference?: string;
  dateFrom?: number;
  dateTo?: number;
  timePeriods?: string[];
  heldBy?: "tna" | "other" | null;
}

export interface NationalArchivesArchiveSearchParams {
  country?: string;
  county?: string;
  page?: number;
  pageSize?: number;
}

export interface NationalArchivesRecord {
  id: string;
  title?: string;
  description?: string;
  reference?: string;
  coveringDates?: string;
  heldBy?: string[];
  startDate?: string;
  endDate?: string;
  source?: string;
}

export interface NationalArchivesSearchResult {
  results: NationalArchivesRecord[];
  total: number;
  page?: number;
  pageSize?: number;
  timePeriods?: Array<{ code: string; count: number }>;
  sources?: Array<{ code: string; count: number }>;
}

export interface NationalArchivesRecordDetails {
  id: string;
  title?: string;
  reference?: string;
  description?: string;
  coveringDates?: string;
  startDate?: number;
  endDate?: number;
  heldBy: Array<{
    id?: string;
    name?: string;
    code?: string;
  }>;
  parentId?: string;
  isParent: boolean;
  catalogueLevel?: number;
  source?: string;
  digitised: boolean;
  accessConditions?: string;
  closureStatus?: string;
  closureType?: string;
  administrativeBackground?: string;
  arrangement?: string;
  custodialHistory?: string;
  relatedMaterial: unknown[];
  separatedMaterial: unknown[];
  people: unknown[];
  places: string[];
  subjects: unknown[];
  corporateNames: unknown[];
  language?: string;
  physicalDescription?: string;
  registryUrl?: string;
  externalUrl: string;
}

export const nationalArchivesApi = {
  /**
   * Search for records in the National Archives with advanced options
   */
  searchRecords: async (
    params: NationalArchivesSearchParams,
    token?: string
  ): Promise<NationalArchivesSearchResult> => {
    const queryParams = new URLSearchParams();

    // Basic params
    if (params.query) queryParams.set("query", params.query);
    queryParams.set("page", String(params.page ?? 1));
    queryParams.set("page_size", String(params.pageSize ?? 20));

    // Advanced search params
    if (params.allWords) queryParams.set("all_words", params.allWords);
    if (params.exactPhrase) queryParams.set("exact_phrase", params.exactPhrase);
    if (params.anyWords) queryParams.set("any_words", params.anyWords);
    if (params.excludingWords)
      queryParams.set("excluding_words", params.excludingWords);
    if (params.reference) queryParams.set("reference", params.reference);
    if (params.dateFrom) queryParams.set("date_from", String(params.dateFrom));
    if (params.dateTo) queryParams.set("date_to", String(params.dateTo));
    if (params.timePeriods && params.timePeriods.length > 0) {
      queryParams.set("time_periods", params.timePeriods.join(","));
    }
    if (params.heldBy) queryParams.set("held_by", params.heldBy);

    return authenticatedRequest(
      {
        method: "GET",
        url: `/national-archives/search/records?${queryParams.toString()}`,
      },
      token
    );
  },

  /**
   * Get details for a specific record
   */
  getRecordDetails: async (
    recordId: string,
    token?: string
  ): Promise<NationalArchivesRecordDetails> => {
    return authenticatedRequest(
      {
        method: "GET",
        url: `/national-archives/records/${recordId}`,
      },
      token
    );
  },

  /**
   * Search for archives and repositories
   */
  searchArchives: async (
    params: NationalArchivesArchiveSearchParams,
    token?: string
  ): Promise<NationalArchivesSearchResult> => {
    const queryParams = new URLSearchParams({
      page: String(params.page ?? 1),
      page_size: String(params.pageSize ?? 20),
    });

    if (params.country) {
      queryParams.append("country", params.country);
    }
    if (params.county) {
      queryParams.append("county", params.county);
    }

    return authenticatedRequest(
      {
        method: "GET",
        url: `/national-archives/search/archives?${queryParams.toString()}`,
      },
      token
    );
  },
};
